import { quickAddActionCreators } from '@app/components/QuickAddModal/modules/quickAdd'
import { Company, ICompany } from '@app/models/Company'
import { IQuickContact } from '@app/models/QuickContact'
import {
  actionCreators,
  IGlobalCompaniesState,
} from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import * as React from 'react'
import Creatable from 'react-select/creatable'
import { ActionMeta, SingleValue } from 'react-select'
import { v4 as uuid } from 'uuid'

export interface ICompanySelectorProps {
  className?: string
  disableQuickAdd?: boolean
  id?: string
  name?: string
  onChange: (newValue?: Company) => void
  placeholder?: string
  required?: boolean
  style?: React.CSSProperties
  tabIndex?: number
  value: ICompany | undefined
}

type CompanySelectorProps = ICompanySelectorProps &
  IGlobalCompaniesState &
  typeof actionCreators &
  typeof quickAddActionCreators

export class CompanySelector extends React.PureComponent<CompanySelectorProps> {
  private instanceId: string
  private selectRef: React.RefObject<HTMLDivElement>

  constructor(props: CompanySelectorProps) {
    super(props)
    this.instanceId = props.id || uuid()
    this.selectRef = React.createRef()
    bindFunctions(this)
  }

  public componentDidMount() {
    this.props.getCompaniesList()
    this._checkValidity()
  }

  public componentDidUpdate() {
    this._checkValidity()
  }

  public render() {
    return (
      <div className={this.props.className} ref={this.selectRef}>
        <Creatable
          tabIndex={this.props.tabIndex}
          formatCreateLabel={this._generateCreatePromptText}
          onChange={this._onChange}
          createOptionPosition='first'
          onCreateOption={this._onCreateOption}
          options={this.props.companies.results.toArray() as Company[]}
          placeholder={this.props.placeholder || 'Select company...'}
          required={this.props.required}
          value={this.props.value as Company}
          // @ts-expect-error - react-select option type mismatch, opt may have either id or value property
          getOptionValue={(opt) => opt.id || opt.value}
          // @ts-expect-error - react-select option type mismatch, opt may have either name or label property
          getOptionLabel={(opt) => opt.name || opt.label}
        />
      </div>
    )
  }

  private _generateCreatePromptText(filterText: string): React.ReactNode {
    return <span>Create a new company for {filterText}</span>
  }

  private _isOptionUnique({
    labelKey,
    option,
    options,
  }: {
    labelKey: string
    option: any
    options: any[]
  }): boolean {
    if (this.props.disableQuickAdd) {
      return false
    }

    if (!options || !options.length) {
      return true
    }

    const testValue = (option[labelKey] as string).toLowerCase()

    for (const opt of options) {
      const optValue = (opt[labelKey] as string).toLowerCase()
      if (optValue.indexOf(testValue) >= 0) {
        return false
      }
    }

    return true
  }

  private _checkValidity() {
    if (this.selectRef.current) {
      const inputElem = this.selectRef.current.querySelector('input:invalid')
      if (inputElem) {
        this.selectRef.current.classList.add('invalid')
      } else {
        this.selectRef.current.classList.remove('invalid')
      }
    }
  }

  private _onChange(value: SingleValue<Company>, action: ActionMeta<Company>) {
    this.props.onChange(value ?? undefined)
  }

  private _onCreateOption(newValue: any) {
    this.props.showQuickAdd(
      this.instanceId,
      newValue.name,
      this._onQuickAddSave,
      true,
    )
  }

  private _onQuickAddSave(contact: IQuickContact) {
    const newCompany = new Company({
      id: contact.contactId,
      name: contact.isCompany
        ? contact.lastName
        : `${contact.firstName} ${contact.lastName}`,
    })
    this.props.onChange(newCompany)
  }
}
